import React from "react";
import { Box, Paper, Typography, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";

/**
 * Heatmap de ESTADOS de alineación (no de similitud numérica).
 *
 * Réplica de graficar_heatmap_alineacion() del notebook del autor: cada celda
 * es el estado categórico del par Master × IES, no un valor continuo.
 *   0 = No similitud (celeste)  1 = Diferencia (azul)  2 = Similitud (azul oscuro)
 *
 * Recibe visualization_data.alignment_heatmap del backend:
 *   { z, x (IES), y (Master), text, value_map, colors }
 */

const HeatmapContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: "none",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

// Paleta del autor (celeste -> azul). Se usa como respaldo si el backend
// no envía `colors` en el payload.
const FALLBACK_COLORS = {
  "No similitud": "#87CEFA",
  Diferencia: "#1E90FF",
  Similitud: "#00008B",
};

const ESTADOS_LEYENDA = ["Similitud", "Diferencia", "No similitud"];

const MaterialAlignmentHeatmap = ({ data }) => {
  if (!data || !data.z || !data.z.length) {
    return (
      <HeatmapContainer>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Sin datos de alineación
          </Typography>
        </Box>
      </HeatmapContainer>
    );
  }

  const colors = { ...FALLBACK_COLORS, ...(data.colors || {}) };
  const iesLabels = data.x || [];
  const masterLabels = data.y || [];

  // Invierte value_map (estado -> numero) para pintar desde z.
  const valueMap = data.value_map || {
    "No similitud": 0,
    Diferencia: 1,
    Similitud: 2,
  };
  const numeroAEstado = Object.entries(valueMap).reduce((acc, [k, v]) => {
    acc[v] = k;
    return acc;
  }, {});

  const estadoDeCelda = (i, j) => {
    // `text` ya trae el estado en texto; si falta, se deriva de z.
    if (data.text && data.text[i] && data.text[i][j]) return data.text[i][j];
    return numeroAEstado[data.z[i][j]] || "No similitud";
  };

  // Celdas cuadradas que se encogen cuando hay muchos términos, con un piso
  // para que sigan siendo visibles (el notebook ajusta figsize de forma similar).
  const maxCols = Math.max(iesLabels.length, 1);
  const cellSize = Math.max(14, Math.min(30, Math.floor(560 / maxCols)));
  const labelWidth = 128;

  return (
    <HeatmapContainer>
      {/* Leyenda de estados */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          justifyContent: "flex-end",
          flexWrap: "wrap",
          mb: 0.5,
          flexShrink: 0,
        }}
      >
        {ESTADOS_LEYENDA.map((estado) => (
          <Box
            key={estado}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                backgroundColor: colors[estado],
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            />
            <Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
              {estado}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Matriz con scroll propio: nunca desborda el contenedor padre */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Box sx={{ display: "inline-block", minWidth: "100%" }}>
          {masterLabels.map((masterName, i) => (
            <Box
              key={`row-${i}`}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Tooltip title={masterName} placement="left">
                <Typography
                  sx={{
                    width: `${labelWidth}px`,
                    minWidth: `${labelWidth}px`,
                    fontSize: "9px",
                    textAlign: "right",
                    pr: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "text.secondary",
                  }}
                >
                  {masterName}
                </Typography>
              </Tooltip>
              {iesLabels.map((iesName, j) => {
                const estado = estadoDeCelda(i, j);
                return (
                  <Tooltip
                    key={`c-${i}-${j}`}
                    title={`${masterName} × ${iesName}: ${estado}`}
                    arrow
                  >
                    <Box
                      sx={{
                        width: `${cellSize}px`,
                        height: `${cellSize}px`,
                        minWidth: `${cellSize}px`,
                        backgroundColor: colors[estado] || FALLBACK_COLORS["No similitud"],
                        border: "1px solid #f0f0f0",
                        cursor: "pointer",
                        transition: "transform 0.15s ease",
                        "&:hover": {
                          transform: "scale(1.15)",
                          zIndex: 2,
                          border: "1px solid #333",
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          ))}

          {/* Etiquetas IES rotadas al pie */}
          <Box sx={{ display: "flex", mt: 0.5 }}>
            <Box sx={{ width: `${labelWidth}px`, minWidth: `${labelWidth}px` }} />
            {iesLabels.map((iesName, j) => (
              <Box
                key={`x-${j}`}
                sx={{
                  width: `${cellSize}px`,
                  minWidth: `${cellSize}px`,
                  height: 90,
                  position: "relative",
                }}
              >
                <Tooltip title={iesName} placement="bottom">
                  <Typography
                    sx={{
                      position: "absolute",
                      top: 4,
                      left: "50%",
                      transformOrigin: "left top",
                      transform: "rotate(60deg)",
                      fontSize: "9px",
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                      maxWidth: 86,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {iesName}
                  </Typography>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: "9px",
          color: "text.secondary",
          textAlign: "center",
          flexShrink: 0,
          pt: 0.5,
        }}
      >
        Filas: MasterDomain · Columnas: IESDomain
      </Typography>
    </HeatmapContainer>
  );
};

export default MaterialAlignmentHeatmap;
